// Tax: inclusive and exclusive pricing, and the GST components.
//
// Spec: spec/06-billing-pricing-and-payments.md §10.1 (the tax engine, F-PAY-11), §1.4 (rounding
// once per line), §1.9 (inclusive by default in India).
//
// Nothing here knows what a tax category is, what an HSN code means or which rate applies: those
// are the organisation's data and its accountant's responsibility (§10.1). This is the arithmetic
// underneath, and it is the same arithmetic on the server, on the invoice, in the kiosk's price
// preview and on the station.
import {} from '@clavian/contracts';
import { assertIntegerInRange } from "../errors.js";
import { addMoney, applyBasisPoints, BASIS_POINTS_SCALE, scaleMoney, splitInHalf, subtractMoney, zeroMoney, } from "../money/index.js";
/** The upper bound on a rate: 100%. Beyond that an inclusive price cannot be decomposed sensibly. */
const MAX_RATE_BPS = 10_000;
/**
 * The tax on one line.
 *
 * - **Exclusive**: `lineAmount` is the taxable value; the tax is added on top.
 * - **Inclusive**: `lineAmount` is what the player pays; the taxable value is backed out of it as
 *   `gross × 10000 / (10000 + rateBps)` and the tax is the remainder, so the two always add back
 *   to the displayed price exactly. Rounding happens once, on the taxable value (spec/06 §1.4);
 *   rounding the tax instead would let ₹500 decompose into something that is not ₹500.
 */
export function computeLineTax(lineAmount, rateBps, pricing) {
    assertIntegerInRange(rateBps, 'rateBps', 0, MAX_RATE_BPS);
    if (pricing === 'exclusive') {
        const taxAmount = applyBasisPoints(lineAmount, rateBps);
        return {
            taxableAmount: lineAmount,
            taxAmount,
            grossAmount: addMoney(lineAmount, taxAmount),
            rateBps,
            pricing,
        };
    }
    const taxableAmount = scaleMoney(lineAmount, BASIS_POINTS_SCALE, BASIS_POINTS_SCALE + rateBps);
    return {
        taxableAmount,
        taxAmount: subtractMoney(lineAmount, taxableAmount),
        grossAmount: lineAmount,
        rateBps,
        pricing,
    };
}
/**
 * A line's tax split into its GST components (spec/06 §10.1).
 *
 * Intra-state supply is CGST + SGST, each half the rate. The **amount** is split with
 * {@link splitInHalf}, not computed twice from the rate: half of an odd number of paise has to go
 * somewhere, and computing each half independently would either lose a paisa or invent one. The
 * larger half is CGST, which is what the worked ledger entry in spec/06 §2.3 shows (₹500 including
 * 18% GST posts CGST −3814 and SGST −3813).
 *
 * Inter-state supply is one IGST component at the whole rate.
 */
export function splitGst(taxAmount, rateBps, place) {
    assertIntegerInRange(rateBps, 'rateBps', 0, MAX_RATE_BPS);
    if (place === 'inter_state') {
        return [{ component: 'IGST', rateBps, amount: taxAmount }];
    }
    const [central, state] = splitInHalf(taxAmount);
    // An odd rate (say 5% = 500 bps → 250 bps each) halves exactly in bps; an odd bps rate does not,
    // and the same "larger half first" rule applies to the rate shown on the invoice.
    const centralRateBps = Math.ceil(rateBps / 2);
    return [
        { component: 'CGST', rateBps: centralRateBps, amount: central },
        { component: 'SGST', rateBps: rateBps - centralRateBps, amount: state },
    ];
}
/** A line's tax, worked out and split in one go — what an order line actually stores. */
export function computeLineTaxComponents(lineAmount, rateBps, pricing, place) {
    const lineTax = computeLineTax(lineAmount, rateBps, pricing);
    return { ...lineTax, components: splitGst(lineTax.taxAmount, rateBps, place) };
}
/**
 * Split a play charge into the revenue and GST a session posts (spec/06 §10.1, §10.3 rule 5).
 *
 * The `charge` is what today goes to `sales_play` — for an inclusive venue (the default) it is also what the
 * player pays, and the tax is backed out of it so the player's amount never changes; for an exclusive venue it
 * is the taxable base and the tax is added on top (`grossAmount > charge`). With no treatment the charge is the
 * whole of `sales_play` and nothing is collected — the behaviour at every unregistered venue and whenever
 * `tax.playTaxed` is off. This is the one place a play charge's tax is computed; the session spine and its
 * reversals both go through it, so inclusive/exclusive, CGST/SGST/IGST and the odd-paisa split all match the
 * pass sale and the shared vectors exactly.
 */
export function taxPlayCharge(charge, treatment) {
    if (treatment === null) {
        return { grossAmount: charge, taxableAmount: charge, taxAmount: zeroMoney(charge.currency), components: [] };
    }
    const taxed = computeLineTaxComponents(charge, treatment.rateBps, treatment.inclusive ? 'inclusive' : 'exclusive', treatment.place);
    return { grossAmount: taxed.grossAmount, taxableAmount: taxed.taxableAmount, taxAmount: taxed.taxAmount, components: taxed.components };
}
// ---------------------------------------------------------------------------------------------
// Place of supply (spec/06 §10.1; F-CMP-07)
// ---------------------------------------------------------------------------------------------
/**
 * The two-digit GST state code a GSTIN begins with (`29` is Karnataka), or `null` when the value is not
 * shaped like one. The check character is the caller's business (`isValidGstin` in `@clavian/contracts`);
 * this reads only the state.
 */
export function gstinStateCode(gstin) {
    if (gstin === null || gstin === undefined)
        return null;
    const trimmed = gstin.trim().toUpperCase();
    return /^[0-9]{2}[0-9A-Z]{13}$/.test(trimmed) ? trimmed.slice(0, 2) : null;
}
/**
 * Where a supply lands, which decides CGST + SGST or IGST (spec/06 §10.1). The venue's state — the state
 * code of its own GSTIN — is the place of supply for services consumed there; a supply is **inter-state**
 * only when a B2B buyer gives a GSTIN registered in a *different* state. With no buyer GSTIN (B2C), no
 * supplier GSTIN (an unregistered venue, which charges no tax anyway), or either not shaped like a GSTIN,
 * the supply is intra-state — the conservative answer, and the one every sale had before.
 */
export function placeOfSupply(supplierGstin, buyerGstin) {
    const supplier = gstinStateCode(supplierGstin);
    const buyer = gstinStateCode(buyerGstin);
    if (supplier === null || buyer === null)
        return 'intra_state';
    return supplier === buyer ? 'intra_state' : 'inter_state';
}
//# sourceMappingURL=index.js.map